import React, {useEffect, useMemo} from 'react';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {clone, get} from 'lodash';
import {useAccountContext} from '../../../contexts/AccountContext';
import {
  AutoFieldProps,
  ActionType,
  AutoField,
} from '../../../components/AutoForm/AutoFieldNew';
import Loading from '../../../components/Loading/Loading';

import {PotDetails} from '../../../schema-types/PotDetails';
import {themeColors} from '../../../theme/Theme';
import {FeeFragment} from '../../../schema-types/FeeFragment';
import {OVPot} from '../../../schema-types/OVPot';
import {PotInfo, getPotInfo} from '../../../utils/getPotInfo';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {builderFieldsType} from './builderFields';
// import {NonStandardFeeCheck} from './useBuilderFeeValidation';
import {useBuilderAvailableFees} from './useBuilderAvailableFees';
import {feesToOptions} from '../../recommendations/CreateRecommend/PlanFees';

export type ongoingFeesProps = {
  formikKeyPrefixWithoutFee: string;
  inModify: boolean;
};

export const BuilderPlanFeesField: React.FC<
  ongoingFeesProps & {
    productId?: string | null;
    pot?: PotDetails | null;
    providerId?: string | null;
    mandateId?: string | null;
    inModify?: boolean;
    requiresMandate: boolean;
    hasSubplan: boolean;
    parentProductId?: string | null;
    pickFpFee?: boolean;
  } & Partial<AutoFieldProps>
> = ({
  formikKeyPrefixWithoutFee,
  disabled,
  hasSubplan,
  productId,
  providerId,
  parentProductId,
  mandateId,
  requiresMandate,
  inModify,
  pot,
  pickFpFee = false,
}) => {
  const {values} = useAutoForm<builderFieldsType>();
  const {account, accountLoading} = useAccountContext();

  // The fee panel is read-only when disabled; the parent owns that state so the inner
  // component never has to reason about it.
  if (disabled) {
    return <DisabledPlanFees />;
  }

  if (!account) {
    if (accountLoading) {
      return (
        <Grid container spacing={2}>
          <Loading entityName="fees" />
        </Grid>
      );
    }
    return null;
  }
  const potInfo = pot ? getPotInfo(pot, values.id) : undefined;

  if (pot?.curoId || potInfo === PotInfo.existingNewPotByOtherRec) {
    if (!inModify) {
      return (
        <Box
          borderRadius="2px"
          border={`1px solid ${themeColors.greyLight}`}
          p={1.5}
          mt={0.5}
          pl={2}
        >
          <Typography variant="body1">Ongoing Fees already set</Typography>
        </Box>
      );
    }
  }

  if (!productId || !providerId || (requiresMandate && !mandateId)) {
    return (
      <Box
        pb={2.5}
        borderRadius="2px"
        border={`2px solid ${themeColors.yellow}`}
        p={2}
        mt={2}
        pl={2}
        boxShadow={2}
      >
        <Typography mb={0.5} variant="body1">
          <strong>Ongoing Fees:</strong>
        </Typography>
        {!productId || !providerId ? (
          <Typography>Please select a product</Typography>
        ) : null}
        {requiresMandate && !mandateId ? (
          <Typography>Please select a mandate</Typography>
        ) : null}
      </Box>
    );
  }

  // The inner component owns the "Set ongoing Fees" panel so that, when there are no
  // composable fees to show, the whole panel (not just the rows) disappears.
  return (
    <BuilderPlanFeesFieldInner
      formikKeyPrefixWithoutFee={formikKeyPrefixWithoutFee}
      productId={productId}
      providerId={providerId}
      mandateId={mandateId}
      hasSubplan={hasSubplan}
      parentProductId={parentProductId}
      pickFpFee={pickFpFee}
    />
  );
};

export type feeExtendedType = FeeFragment & {
  calculated?: boolean;
  deviation?: boolean;
  used?: boolean;
};

type availableFeesExtendedType = {
  fees: feeExtendedType[];
  required: boolean | null;
};

export type feeDeciderExtendedType = {
  fpFees: availableFeesExtendedType;
  dfmFees: availableFeesExtendedType;
  productFees: availableFeesExtendedType;
  sippFees: availableFeesExtendedType;
};

// Read-only fee row: the fee name as plain text + its tier/flat-rate breakdown.
const ReadOnlyFeeRow: React.FC<{fee: FeeFragment | null; label: string}> = ({
  fee,
  label,
}) => (
  <TableRow>
    <TableCell component="th" scope="row">
      {label}
    </TableCell>
    <TableCell>{fee?.name ?? '—'}</TableCell>
    <TableCell align="center">
      <Tiers fee={fee} />
    </TableCell>
  </TableRow>
);

// SPP-only editable FP fee row: a single fixed percentage, so no tier detail is shown.
const EditableFpFeeRow: React.FC<{label: string; fieldId: string}> = ({
  label,
  fieldId,
}) => (
  <TableRow>
    <TableCell component="th" scope="row">
      {label}
    </TableCell>
    <TableCell>
      <AutoField
        field={{
          id: fieldId,
          label: 'Percentage',
          type: ActionType.text,
          allowNegative: false,
          percent: true,
          number: true,
          decimals: 2,
        }}
      />
    </TableCell>
    <TableCell />
  </TableRow>
);

const PickableFeeFormRow: React.FC<{
  children: React.ReactNode;
  charges?: React.ReactNode;
}> = ({children, charges}) => (
  <Box borderBottom={`1px solid ${themeColors.white}`}>
    <Grid container alignItems="center" columnSpacing={1} pt={2} pb={2}>
      <Grid item xs={6}>
        {children}
      </Grid>
      <Grid item xs textAlign="center">
        {charges}
      </Grid>
    </Grid>
  </Box>
);

// Same layout as the BAU PlanFeesFieldInner, for a wrapper whose Off Platform FP fee the adviser picks.
const PickableFpFeeForm: React.FC<{
  fieldId: string;
  fpFees: feeExtendedType[];
  dfmFee: FeeFragment | null;
  productFee: FeeFragment | null;
}> = ({fieldId, fpFees, dfmFee, productFee}) => {
  const {values} = useAutoForm<builderFieldsType>();
  const options = useMemo(
    () => feesToOptions(fpFees.map((fee) => ({...fee, calculated: true}))),
    [fpFees]
  );
  const pickedFee = fpFees.find((fee) => fee.id === get(values, fieldId)) ?? null;
  return (
    <>
      <PickableFeeFormRow charges={<Tiers fee={dfmFee} />}>
        <Typography pl={1.5}>{dfmFee?.name ?? 'No DFM Fee required'}</Typography>
      </PickableFeeFormRow>
      <PickableFeeFormRow charges={<Tiers fee={pickedFee} />}>
        <AutoField
          field={{
            id: fieldId,
            label: 'FP Fee',
            type: ActionType.selectAutocomplete,
            options,
          }}
        />
      </PickableFeeFormRow>
      <PickableFeeFormRow charges={<Tiers fee={productFee} />}>
        <Typography pl={1.5}>
          {productFee?.name ?? 'Product fee determined by provider'}
        </Typography>
      </PickableFeeFormRow>
    </>
  );
};

// Rendered by the parent (BuilderPlanFeesField) when the fee panel is disabled.
const DisabledPlanFees: React.FC = () => null; // TODO: disabled fee view

export const BuilderPlanFeesFieldInner: React.FC<{
  formikKeyPrefixWithoutFee: string;
  productId: string;
  providerId: string;
  mandateId?: string | null;
  hasSubplan: boolean;
  parentProductId?: string | null;
  pickFpFee?: boolean;
}> = ({
  formikKeyPrefixWithoutFee: formikKeyPrefix,
  productId,
  providerId,
  mandateId,
  parentProductId,
  hasSubplan,
  pickFpFee = false,
}) => {
  const {values} = useAutoForm<builderFieldsType>();
  // The editable SPP FP fee percentage is only captured on the individual plan, never
  // on a wrapper that has sub-plans (each sub-plan captures its own percentage).
  const showEditableFpFee = !!values.isSppDa && !hasSubplan;
  const planFeeKey = `${formikKeyPrefix}.planFees`;
  const {account} = useAccountContext();
  const {data, feesLoading, getAvailableFees} = useBuilderAvailableFees();

  // Fees come straight from the dentist availableFeeComponents query (one relevant fee
  // per category). The fetch is keyed on the parent-supplied product / mandate / provider
  // props, so a change re-runs the query and `feesLoading` shows a loading state until the
  // fresh fees arrive — the previous mandate's rows are never displayed.
  useEffect(() => {
    const accountId = account?.id;
    if (!productId || !providerId || !accountId) {
      return;
    }
    getAvailableFees({
      variables: {
        productId,
        mandateId,
        providerId,
        parentProductId,
        accountId,
        hasSubplan,
      },
    });
  }, [
    hasSubplan,
    productId,
    mandateId,
    providerId,
    parentProductId,
    getAvailableFees,
    account?.id,
  ]);

  const fees: feeDeciderExtendedType | null = data?.availableFeeComponentsDentist ?? null;
  const dfmFee = fees?.dfmFees?.fees?.[0] ?? null;
  const fpFee = fees?.fpFees?.fees?.[0] ?? null;
  const productFee = fees?.productFees?.fees?.[0] ?? null;
  // always show sipp fees, because backend already decides whether to return it or not
  const sippFee = fees?.sippFees?.fees?.[0] ?? null;

  // The ongoing fee IDs (DFM/FP/product) are composed by the backend, so the client no
  // longer stores or sends them. The SPP FP fee percentage is captured but intentionally
  // NOT pre-filled — the adviser enters it (the sub-plan validator requires it).

  if (!fees || feesLoading) {
    return <Loading entityName="fees" />;
  }

  // A wrapper such as a GIA/ISA over sub-plans has no composable ongoing fees. When there
  // is nothing to show (and no editable SPP FP input), render nothing rather than a table
  // of empty rows.
  if (!dfmFee && !fpFee && !productFee && !sippFee && !showEditableFpFee) {
    return null;
  }

  return (
    <Box
      pb={2.5}
      boxShadow={2}
      borderRadius="2px"
      mt={2}
      p={2}
      flex={1}
      border={`2px solid ${themeColors.lightBlue}`}
    >
      <Box display="flex" justifyContent="initial" alignItems="center">
        <Typography variant="body1">
          <strong>Set ongoing Fees:</strong>
        </Typography>
      </Box>
      {pickFpFee && fpFee && !showEditableFpFee ? (
        <PickableFpFeeForm
          fieldId={`${planFeeKey}.fpFeeId`}
          fpFees={fees.fpFees.fees}
          dfmFee={dfmFee}
          productFee={productFee}
        />
      ) : (
        <TableContainer>
          <Table size="small" aria-label="ongoing fees">
            <TableHead>
              <TableRow>
                <TableCell>Fee</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="center">Charges</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <ReadOnlyFeeRow fee={dfmFee} label="DFM Fee" />
              {showEditableFpFee ? (
                <EditableFpFeeRow
                  label="FP Fee"
                  fieldId={`${planFeeKey}.sppFpFeePercentage`}
                />
              ) : (
                <ReadOnlyFeeRow fee={fpFee} label="FP Fee" />
              )}
              <ReadOnlyFeeRow fee={productFee} label="Product Fee" />
              {sippFee ? <ReadOnlyFeeRow fee={sippFee} label="SIPP Fee" /> : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* <NonStandardFeeCheck fees={fees} formikKeyPrefixWithoutFees={formikKeyPrefix} /> */}
    </Box>
  );
};

export const Tiers: React.FC<{fee?: FeeFragment | null}> = ({fee}) => {
  if (!fee) {
    return null;
  }
  if (!fee.calculationBasis?.toLocaleLowerCase()?.includes('percentage')) {
    return (
      <Box>
        Flat rate - {fee.frequency} £{fee.amount}
      </Box>
    );
  }
  if (!fee.tiers) {
    return (
      <TiersDisplay
        fees={[
          fee.annualPercentage,
          fee.annualPercentage,
          fee.annualPercentage,
          fee.annualPercentage,
        ]}
      />
    );
  }
  return <TiersDisplay fees={[fee.tiers[0], fee.tiers[1], fee.tiers[2], fee.tiers[3]]} />;
};

export const TiersDisplay: React.FC<{
  fees: [
    string | number | null,
    string | number | null,
    string | number | null,
    string | number | null
  ];
}> = ({fees}) => {
  return (
    <>
      <Grid
        width="100%"
        flex={1}
        container
        spacing={1}
        borderBottom={`1px solid inline ${themeColors.greyLight}`}
      >
        <Grid item boxSizing="border-box" width="25%" textAlign="center">
          <Typography variant="overline">
            <i>0-500K:</i>
          </Typography>
          <Typography>
            <strong>{fees[0]}%</strong>
          </Typography>
        </Grid>
        <Grid item boxSizing="border-box" width="25%" textAlign="center">
          <Typography variant="overline">
            <i>500K-1M:</i>
          </Typography>
          <Typography>
            <strong>{fees[1]}%</strong>
          </Typography>
        </Grid>
        <Grid item boxSizing="border-box" width="25%" textAlign="center">
          <Typography variant="overline">
            <i>1M-2M:</i>
          </Typography>
          <Typography>
            <strong>{fees[2]}%</strong>
          </Typography>
        </Grid>
        <Grid item boxSizing="border-box" width="25%" textAlign="center">
          <Typography variant="overline">
            <i>2M+</i>
          </Typography>
          <Typography>
            <strong>{fees[3]}%</strong>
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

export const defaultFeeFields: planFeesFieldType = {
  sppFpFeePercentage: null,
  fpFeeId: null,
};

export const getPlanFeesFormikFromServer = <
  T extends Pick<OVPot, 'sppOngoingFpFeePercentage'>
>(
  ovPot?: T | null
): planFeesFieldType | undefined => {
  const feeFields = clone(defaultFeeFields);
  if (!ovPot) {
    return undefined;
  }
  // For SPP plans the FP fee is captured as a percentage; the ongoing fee IDs
  // themselves are composed by the backend and no longer round-tripped through
  // the client.
  if (ovPot.sppOngoingFpFeePercentage != null) {
    feeFields.sppFpFeePercentage = String(ovPot.sppOngoingFpFeePercentage);
  }
  return feeFields;
};

export type planFeesFieldType = {
  // Ongoing FP fee percentage captured for SPP plans (e.g. 0.15 == 0.15%).
  sppFpFeePercentage: string | null;
  // The Off Platform FP fee the adviser picked for a bond wrapper.
  fpFeeId: string | null;
};
